// api.js
import axios from "axios";

// Create Axios instance
const apiClient = axios.create({
  baseURL: "https://zola.technology/api/",
  headers: {
    "Content-Type": "application/json",
  },
});

// =================== Auth ===================

export const registerUser = async (userData) => {
  try {
    const response = await apiClient.post("company/signup/", userData);
    return response;
  } catch (error) {
    const data = error.response?.data;
    const message =
      data?.username?.[0] ||
      data?.email?.[0] ||
      data?.phone_number?.[0] ||
      error.message ||
      "Error registering user";
    throw new Error(message);
  }
};

export const getToken = async (credentials) => {
  try {
    const response = await apiClient.post("token/", credentials);
    return response;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "Error fetching token");
  }
};

export const refreshToken = async (refreshToken) => {
  try {
    const response = await apiClient.post("token/refresh/", { refresh: refreshToken });
    return response;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "Error refreshing token");
  }
};

// =================== User & Company ===================

export const getUserProfile = (username, token) =>
  apiClient.get(`company/get/${username}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const getCompanyProfile = async (token) => {
  try {
    const response = await apiClient.get("company/profile/", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "Error fetching company profile");
  }
};

export const listCompanies = async (token) => {
  try {
    const response = await apiClient.get("company/get-all/", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "Error fetching companies");
  }
};

// =================== Employees ===================

export const addEmployees = (employees, token) =>
  apiClient.post("company/add_employees", { employees }, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const deleteEmployee = (username, token) =>
  apiClient.delete(`company/delete/${username}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

// =================== Payment ===================

export const makePayment = async (
  username,
  total,
  phone_number,
  transactionType,
  subscriptionType,
  subscriptionAmount
) => {
  try {
    const response = await apiClient.post("company/make-payment/", {
      username,
      unit_amount: total - subscriptionAmount,
      phone_number,
      subscription_interval: subscriptionType,
      subscription_amount: subscriptionAmount,
      transaction_type: transactionType,
    });
    return response.data.url;
  } catch (error) {
    throw new Error(error.response?.data?.error);
  }
};

// =================== Feedback ===================

export const addFeedback = async (feedbackData) => {
  try {
    const { feedback_channel, ...payload } = feedbackData;
    const response = await apiClient.post("company/feedback/add/", payload, {
      params: { feedback_channel },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "Error adding feedback");
  }
};

export const listFeedback = async (currentPage, pageSize) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Token not found");
  try {
    const response = await apiClient.get(
      `company/feedback/list/?page=${currentPage}&page_size=${pageSize}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "Error fetching feedback");
  }
};

export const feedBackList = async () => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Token not found");
  try {
    const response = await apiClient.get("company/feedback/list-categorised/", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "Error fetching feedback");
  }
};

export const listFeedbackWithoutParams = async () => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Token not found");
  try {
    const response = await apiClient.get("company/feedback/list/", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "Error fetching feedback");
  }
};

// =================== Reviews ===================

export const listReviews = async (token) => {
  try {
    const response = await apiClient.get("company/reviews/list", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "Error fetching reviews");
  }
};

// =================== Analytics ===================

export const getLinkInteractions = async (token, selectedPeriod) => {
  try {
    const response = await apiClient.get(
      `company/employees/analytics/get-all-with-taps/?filter=${selectedPeriod}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "Error fetching link interactions");
  }
};


